import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import DevicesOtherOutlinedIcon from '@mui/icons-material/DevicesOtherOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import { type GridColDef, type GridSortModel } from '@mui/x-data-grid';
import CustomStyledDataGrid from 'components/MuiDataGrid/CustomStyledDataGrid';
import {
  LocationZoneMapModal,
  type ZoneMapLocation,
} from 'components/Map/LocationZoneMapModal';
import { useAppSelector } from 'store/hook';
import { selectAssociatedAssets } from 'store/asset.slice';
import DeviceAPI from 'api/device.api';
import DispatchMonitorAPI from 'api/dispatch-monitor.api';
import LocationImporterAPI, {
  type ImportedLocation,
} from 'api/location-importer.api';
import { type DeviceLatestSensorTimestamp } from 'types/device.types';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { useDeviceLastSensorSSE } from 'hooks/useDeviceLastSensorSSE';
import {
  useTabletHeartbeatSSE,
  type TabletHeartbeat,
} from 'hooks/useTabletHeartbeatSSE';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

interface NearestLocation {
  locationId: string;
  name: string;
  distanceMeters: number;
}

interface AssetInfoEntry {
  symbotId: string;
  bumperNumber: string;
  assetType: string;
  makeModelDetails: { make: string; model: string } | null;
  lat: number;
  lon: number;
  nearestLocation: NearestLocation | null;
}

function formatTimeAgo(ts: number, t: (key: string, opts?: any) => any) {
  const diffMs = Date.now() - ts;
  if (diffMs < 0) return '—';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return String(t('devices_tab_time_ago_sec', { count: sec }));
  const min = Math.floor(sec / 60);
  if (min < 60) return String(t('devices_tab_time_ago_min', { count: min }));
  const hr = Math.floor(min / 60);
  if (hr < 24) return String(t('devices_tab_time_ago_hr', { count: hr }));
  const days = Math.floor(hr / 24);
  return String(t('devices_tab_time_ago_day', { count: days }));
}

interface DeviceRow {
  id: string;
  device: string;
  bumperId: string;
  lastSensorTime: number;
  nearestLocation: NearestLocation | null;
  tabletLastSync: number | null;
}

export const DevicesTab = () => {
  const { t } = useTranslation();
  const api = React.useRef(new DeviceAPI()).current;
  const dispatchApi = React.useRef(new DispatchMonitorAPI()).current;
  const locationApi = React.useRef(new LocationImporterAPI()).current;
  const associatedAssets = useAppSelector(selectAssociatedAssets);
  const [loading, setLoading] = React.useState(true);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [sortModel, setSortModel] = React.useState<GridSortModel>([
    { field: 'bumperId', sort: 'asc' },
  ]);
  const [timestamps, setTimestamps] = React.useState<
    DeviceLatestSensorTimestamp[]
  >([]);
  const [liveTimestamps, setLiveTimestamps] = React.useState<
    Record<string, number>
  >({});
  const [assetInfoBySymbotId, setAssetInfoBySymbotId] = React.useState<
    Record<string, AssetInfoEntry>
  >({});
  const [locationsById, setLocationsById] = React.useState<
    Map<string, ImportedLocation>
  >(new Map());
  const [mapLocation, setMapLocation] = React.useState<ZoneMapLocation | null>(
    null
  );
  const closeMapModal = React.useCallback(() => {
    setMapLocation(null);
  }, []);
  // Keyed by device — receivedAt is stamped locally the moment the SSE
  // message arrives, not read from the tablet's own payload, since the
  // tablet's clock can be skewed (same reason lastSensorTime below flags
  // clock skew rather than trusting the device-reported value outright).
  const [tabletHeartbeats, setTabletHeartbeats] = React.useState<
    Record<string, TabletHeartbeat & { receivedAt: number }>
  >({});
  const [, forceTick] = React.useReducer((x: number) => x + 1, 0);
  const { timezoneBasedOnDisplayMode, preferredTimeDisplayMode } =
    useTimeDisplayMode();

  React.useEffect(() => {
    const id = setInterval(() => {
      forceTick();
    }, 1000);
    return () => {
      clearInterval(id);
    };
  }, []);

  const fetchTimestamps = React.useCallback(async () => {
    setLoading(true);
    try {
      const [sensorData, assetInfoRes, locations] = await Promise.all([
        api.latestSensorTimestamps(),
        dispatchApi.getAssetInfo(),
        locationApi.getLocations(),
      ]);
      setTimestamps(sensorData);
      const bySymbotId: Record<string, AssetInfoEntry> = {};
      (assetInfoRes.assets ?? []).forEach((a: AssetInfoEntry) => {
        bySymbotId[a.symbotId] = a;
      });
      setAssetInfoBySymbotId(bySymbotId);
      const byId = new Map<string, ImportedLocation>();
      locations.forEach((loc) => {
        byId.set(loc.locationId, loc);
      });
      setLocationsById(byId);
    } finally {
      setLoading(false);
    }
  }, [api, dispatchApi, locationApi]);

  React.useEffect(() => {
    void fetchTimestamps();
  }, [fetchTimestamps]);

  useDeviceLastSensorSSE(
    React.useCallback((deviceId: string, latestTs: number) => {
      setLiveTimestamps((prev) => {
        if (prev[deviceId] >= latestTs) return prev;
        return { ...prev, [deviceId]: latestTs };
      });
    }, [])
  );

  useTabletHeartbeatSSE(
    React.useCallback((heartbeat: TabletHeartbeat) => {
      setTabletHeartbeats((prev) => ({
        ...prev,
        [heartbeat.device]: { ...heartbeat, receivedAt: Date.now() },
      }));
    }, [])
  );

  const rows = React.useMemo<DeviceRow[]>(() => {
    const latestByDevice = new Map<string, number>();
    timestamps.forEach((entry) => {
      const current = latestByDevice.get(entry.device);
      if (current === undefined || entry.timestamp > current) {
        latestByDevice.set(entry.device, entry.timestamp);
      }
    });
    Object.entries(liveTimestamps).forEach(([device, ts]) => {
      const current = latestByDevice.get(device);
      if (current === undefined || ts > current) {
        latestByDevice.set(device, ts);
      }
    });

    const allDevices = new Set([
      ...latestByDevice.keys(),
      ...Object.keys(tabletHeartbeats),
    ]);

    return [...allDevices]
      .map((device) => {
        const asset = associatedAssets.find(
          (a) => a.device?.deviceId?.toLowerCase() === device?.toLowerCase()
        );
        const bumperId = asset?.bumperNumber ?? '—';
        return {
          id: device,
          device,
          bumperId,
          lastSensorTime: latestByDevice.get(device) ?? 0,
          tabletLastSync: tabletHeartbeats[device]?.receivedAt ?? null,
          nearestLocation:
            bumperId !== '—'
              ? assetInfoBySymbotId[device]?.nearestLocation ?? null
              : null,
        };
      })
      .filter((row) => row.bumperId && row.bumperId !== '—');
  }, [
    timestamps,
    liveTimestamps,
    tabletHeartbeats,
    assetInfoBySymbotId,
    associatedAssets,
  ]);

  const filteredRows = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.device?.toLowerCase().includes(q) ||
        r.bumperId?.toLowerCase().includes(q)
    );
  }, [rows, searchQuery]);

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: 'device',
        headerName: t('devices_tab_col_device'),
        minWidth: 220,
        flex: 1,
        filterable: true,
        sortable: true,
        renderCell: ({ value }: { value?: string }) => {
          const isLive = !!value && liveTimestamps[value] !== undefined;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>{value}</span>
              {isLive && (
                <Tooltip title={t('devices_tab_live_tooltip')}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: '#22c55e',
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      {
        field: 'bumperId',
        headerName: t('bumper_id_10'),
        minWidth: 160,
        flex: 1,
        filterable: true,
        sortable: true,
        sortComparator: (v1: string, v2: string) => {
          if (v1 === '—' && v2 === '—') return 0;
          if (v1 === '—') return 1;
          if (v2 === '—') return -1;
          return v1.localeCompare(v2, undefined, {
            numeric: true,
            sensitivity: 'base',
          });
        },
      },
      {
        field: 'nearestLocation',
        headerName: t('devices_tab_col_last_location'),
        minWidth: 220,
        flex: 1.2,
        filterable: true,
        sortable: true,
        valueGetter: ({ value }: { value: NearestLocation | null }) =>
          value?.name ?? '',
        renderCell: ({ row }: { row: DeviceRow }) => {
          const nearest = row.nearestLocation;
          if (!nearest) return '—';
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <span>{nearest.name}</span>
              <Tooltip title={t('devices_tab_view_on_map')}>
                <IconButton
                  size="small"
                  onClick={() => {
                    const fullLocation =
                      locationsById.get(nearest.locationId) ?? null;
                    const assetInfo = assetInfoBySymbotId[row.device];
                    setMapLocation({
                      name: fullLocation?.name ?? nearest.name,
                      locationType: fullLocation?.locationType,
                      centroidLat: fullLocation?.centroidLat ?? null,
                      centroidLon: fullLocation?.centroidLon ?? null,
                      zoneGeog: fullLocation?.zoneGeog ?? null,
                      asset: assetInfo
                        ? {
                            bumperNumber: row.bumperId,
                            assetType: assetInfo.assetType,
                            make: assetInfo.makeModelDetails?.make,
                            model: assetInfo.makeModelDetails?.model,
                            lat: assetInfo.lat,
                            lon: assetInfo.lon,
                          }
                        : null,
                    });
                  }}
                  sx={{ p: 0.25, color: 'text.secondary' }}
                >
                  <FmdGoodOutlinedIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
      {
        field: 'lastSensorTime',
        headerName: t('devices_tab_col_last_sensor_time'),
        minWidth: 240,
        flex: 1,
        type: 'dateTime',
        filterable: true,
        sortable: true,
        valueGetter: ({ value }: { value?: number }) =>
          value ? new Date(value) : null,
        valueFormatter: ({ value }: { value?: Date }) =>
          value
            ? convertUtcToDisplayTime(
                value,
                preferredTimeDisplayMode,
                timezoneBasedOnDisplayMode
              )
            : '—',
        renderCell: ({ value }: { value?: Date }) => {
          if (!value) return '—';
          const formatted = convertUtcToDisplayTime(
            value,
            preferredTimeDisplayMode,
            timezoneBasedOnDisplayMode
          );
          const isClockSkewed = value.getTime() > Date.now() + ONE_DAY_MS;
          if (!isClockSkewed) return formatted;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <span>{formatted}</span>
              <Tooltip title={t('devices_tab_clock_skew_tooltip')}>
                <WarningAmberOutlinedIcon
                  sx={{ fontSize: 16, color: 'warning.main' }}
                />
              </Tooltip>
            </Box>
          );
        },
      },
      {
        field: 'timeAgo',
        headerName: t('devices_tab_col_time_ago'),
        minWidth: 140,
        flex: 0.7,
        filterable: false,
        sortable: true,
        valueGetter: ({ row }: { row: DeviceRow }) => row.lastSensorTime || 0,
        renderCell: ({ row }: { row: DeviceRow }) =>
          row.lastSensorTime ? formatTimeAgo(row.lastSensorTime, t) : '—',
      },
      {
        field: 'tabletLastSync',
        headerName: t('devices_tab_col_tablet_last_sync'),
        minWidth: 160,
        flex: 0.7,
        filterable: false,
        sortable: true,
        valueGetter: ({ row }: { row: DeviceRow }) => row.tabletLastSync ?? 0,
        renderCell: ({ row }: { row: DeviceRow }) =>
          row.tabletLastSync ? formatTimeAgo(row.tabletLastSync, t) : '—',
      },
    ],
    [
      t,
      preferredTimeDisplayMode,
      timezoneBasedOnDisplayMode,
      liveTimestamps,
      locationsById,
      assetInfoBySymbotId,
    ]
  );

  const hasData = rows.length > 0;
  const hasResults = filteredRows.length > 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minWidth: 0,
        height: 'calc(100vh - 260px)',
        border: '1px solid',
        borderColor: (th) => alpha(th.palette.divider, 0.5),
        borderRadius: '10px',
        overflow: 'hidden',
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: (th) => alpha(th.palette.divider, 0.5),
          flexShrink: 0,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} color="text.primary">
            {t('devices_tab')}
          </Typography>
          {hasData && (
            <Typography
              variant="caption"
              sx={{
                px: 1.2,
                py: 0.3,
                borderRadius: '20px',
                bgcolor: (th) => alpha(th.palette.primary.main, 0.1),
                color: 'primary.main',
                fontWeight: 700,
              }}
            >
              {searchQuery.trim()
                ? `${filteredRows.length} / ${rows.length}`
                : rows.length}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            placeholder={t('devices_tab_search_placeholder')}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchQuery('');
                    }}
                    edge="end"
                    sx={{ p: 0.25 }}
                  >
                    <ClearIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              width: 260,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                fontSize: '0.82rem',
              },
            }}
          />
          <Tooltip title={t('devices_tab_refresh')}>
            <IconButton
              size="small"
              onClick={() => {
                void fetchTimestamps();
              }}
              disabled={loading}
              sx={{
                color: 'text.secondary',
                animation: loading ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            >
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {loading && rows.length === 0 && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}

        {!loading && !hasData && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: (th) => alpha(th.palette.primary.main, 0.1),
                border: '1.5px solid',
                borderColor: (th) => alpha(th.palette.primary.main, 0.3),
                mb: 1,
              }}
            >
              <DevicesOtherOutlinedIcon
                sx={{ fontSize: 32, color: 'primary.main' }}
              />
            </Box>
            <Typography variant="h6" fontWeight={700} color="text.primary">
              {t('devices_tab_empty_title')}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ maxWidth: 300, lineHeight: 1.7 }}
            >
              {t('devices_tab_empty_subtitle')}
            </Typography>
          </Box>
        )}

        {hasData && !hasResults && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <SearchIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
            <Typography variant="body2" color="text.secondary">
              {t('devices_tab_no_results')}
            </Typography>
          </Box>
        )}

        {hasData && hasResults && (
          <CustomStyledDataGrid
            rows={filteredRows}
            columns={columns}
            disableRowSelectionOnClick
            getRowHeight={() => 48}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            sx={{ flex: 1, minWidth: 0, overflow: 'auto', border: 'none' }}
          />
        )}
      </Box>

      <LocationZoneMapModal
        open={Boolean(mapLocation)}
        onClose={closeMapModal}
        location={mapLocation}
      />
    </Box>
  );
};
